/**
 * Classify a roster shift's free-text service value into a stable display family.
 *
 * WHY THIS EXISTS. `roster_shifts.service_type` is not a controlled vocabulary and
 * never has been. A census of live rows (2026-08-03, all tenants) returns values
 * that vary by case (`personal_care` / `PERSONAL_CARE` / `Personal Care`), carry
 * the roster line's hours inline (`RN 07.00 - 15.00`, `Laundry 13.30 - 16.30`),
 * gloss themselves (`RN (registered nurse)`), name non-clinical functions
 * (`Catering`, `Administration`, `Maintenance`), and — for Support at Home
 * providers — use the SAH service categories (`INDEPENDENCE`, `EVERYDAY_LIVING`).
 *
 * The roster board previously keyed colour off an exact uppercase match against a
 * six-entry map. Every value above missed it and fell through to one grey. For a
 * Support at Home provider that is 100% of the board: colour was present in the
 * code and absent on the screen.
 *
 * THE CONTRACT. This classifies for DISPLAY ONLY. It never writes back, never
 * feeds care-minutes eligibility, funding, claiming or pay, and must not be used
 * to decide any of those. `service_type` is a care-minutes input
 * (`service_type_care_minutes_eligibility`), so normalising the stored data is a
 * regulated change with an AN-ACC blast radius and is deliberately out of scope
 * here. This makes the board legible without touching a single row.
 *
 * UNKNOWN IS NOT UNCLASSIFIED. Two distinct families are kept apart on purpose:
 *   - `UNKNOWN` — a value we could not place. It still gets a stable, distinct
 *     colour derived from the text, so two different unrecognised services stay
 *     visually different instead of collapsing into one grey.
 *   - `UNSET` — no value at all. 86 live Golden Bloom shifts carry no
 *     `service_type`. Rendering those identically to "other" hides a data gap the
 *     coordinator should see, so they get their own explicitly-unset treatment.
 */

/** Display families the board can colour. Not a data vocabulary. */
export type RosterServiceFamily =
  | 'CLINICAL'
  | 'NURSING'
  | 'PERSONAL_CARE'
  | 'DOMESTIC'
  | 'SOCIAL_SUPPORT'
  | 'ALLIED_HEALTH'
  | 'TRANSPORT'
  | 'CARE_ROUND'
  | 'CATERING'
  | 'ADMINISTRATION'
  | 'MAINTENANCE'
  | 'INDEPENDENCE'
  | 'EVERYDAY_LIVING'
  | 'UNKNOWN'
  | 'UNSET';

/**
 * Reduce a raw value to a comparable token.
 *
 * Strips the trailing hour range some tenants append to the service name, drops
 * parenthetical glosses, and folds case, punctuation and spacing to underscores.
 * `RN 07.00 - 15.00` and `RN (registered nurse)` both reduce to `RN`.
 */
export function normaliseServiceToken(raw: string | null | undefined): string {
  if (raw == null) return '';
  let value = String(raw).trim();
  if (!value) return '';
  // Drop a trailing time range: "07.00 - 15.00", "13:30-16:30", "7.00–15.00".
  // Only when something is left: a value that is ONLY an hour range carries no
  // service meaning to recover, but blanking it would report UNSET — "no
  // service recorded" — when a value is in fact recorded. Keep the original so
  // it classifies as UNKNOWN, which is the truthful answer.
  const withoutTimeRange = value.replace(
    /[\s,-]*\d{1,2}[.:]\d{2}\s*[-–—]\s*\d{1,2}[.:]\d{2}\s*$/u,
    '',
  );
  if (withoutTimeRange.trim()) value = withoutTimeRange;
  // Drop parenthetical glosses: "RN (registered nurse)" -> "RN".
  value = value.replace(/\([^)]*\)/gu, ' ');
  return value
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/gu, '_')
    .replace(/^_+|_+$/gu, '');
}

/**
 * Exact token matches, checked before the substring pass.
 *
 * Order matters only in that this map wins: `EN` must not be reached by a
 * substring rule, and short role codes like `RN`/`PCA` are too small to match
 * safely by inclusion.
 */
const EXACT_TOKENS: Record<string, RosterServiceFamily> = {
  // Support at Home service categories.
  INDEPENDENCE: 'INDEPENDENCE',
  EVERYDAY_LIVING: 'EVERYDAY_LIVING',
  EVERYDAY_LIVING_SUPPORT: 'EVERYDAY_LIVING',
  CLINICAL: 'CLINICAL',
  CLINICAL_CARE: 'CLINICAL',
  // Nursing role codes.
  RN: 'NURSING',
  EN: 'NURSING',
  LEAD_RN: 'NURSING',
  AIN: 'PERSONAL_CARE',
  PCA: 'PERSONAL_CARE',
  // Residential rounds.
  CARE_ROUND: 'CARE_ROUND',
  NIGHT_SHIFT: 'CARE_ROUND',
  RESIDENTIAL_CARE: 'CARE_ROUND',
  COORDINATION: 'ADMINISTRATION',
  ADMINISTRATION: 'ADMINISTRATION',
  ADMIN: 'ADMINISTRATION',
  RECEPTION: 'ADMINISTRATION',
  RECEPTIONIST: 'ADMINISTRATION',
  FRONT_DESK: 'ADMINISTRATION',
  COOK: 'CATERING',
  CHEF: 'CATERING',
  // The platform's own controlled care-minutes vocabulary
  // (`service_type_care_minutes_eligibility`). These are legitimate service
  // types the board must be able to colour even where no tenant has rostered
  // one yet, so they are pinned here rather than left to fall through.
  BEHAVIOUR_SUPPORT: 'CLINICAL',
  HANDOVER: 'ADMINISTRATION',
  TRAINING: 'ADMINISTRATION',
  HOSPITALITY: 'CATERING',
};

/**
 * Substring rules, first match wins.
 *
 * Deliberately ordered most-specific first: `RN_MEDICATION_ROUND` must reach
 * NURSING via `MEDICATION` before `ROUND` could pull it to CARE_ROUND, and
 * `PERSONAL_CARE` must be tested before the bare `CARE` fallback.
 */
const SUBSTRING_RULES: ReadonlyArray<readonly [string, RosterServiceFamily]> = [
  // Support at Home categories first: a provider writing "Independence support"
  // or "Everyday living assistance" means the SAH category, and DOMESTIC or
  // SOCIAL_SUPPORT below would otherwise claim those strings.
  // 1. Support at Home categories. A provider writing "Independence support"
  //    means the SAH category, and DOMESTIC or SOCIAL_SUPPORT would claim it.
  ['EVERYDAY_LIVING', 'EVERYDAY_LIVING'],
  ['INDEPENDENCE', 'INDEPENDENCE'],

  // 2. Direct care on the person. MEALTIME must precede MEAL: assisting a
  //    resident to eat is personal care, not kitchen work, and "Mealtime
  //    assistance" would otherwise be filed under CATERING.
  ['PERSONAL_CARE', 'PERSONAL_CARE'],
  ['MEALTIME', 'PERSONAL_CARE'],
  ['ALLIED_HEALTH', 'ALLIED_HEALTH'],

  // 3. Client-facing lifestyle and social programmes. These sit ABOVE the
  //    facility-function words below so a programme named after a place or
  //    chore keeps its meaning: "Garden activities" is a lifestyle programme,
  //    not grounds maintenance, and "Domestic respite" is respite.
  ['SOCIAL_SUPPORT', 'SOCIAL_SUPPORT'],
  ['LIFESTYLE', 'SOCIAL_SUPPORT'],
  ['ACTIVITIES', 'SOCIAL_SUPPORT'],
  ['RESPITE', 'SOCIAL_SUPPORT'],
  ['COMPANION', 'SOCIAL_SUPPORT'],

  // 4. Clinical and nursing. MEDICATION precedes the round words so
  //    "RN medication round" is nursing rather than a generic care round.
  ['MEDICATION', 'NURSING'],
  ['WOUND', 'CLINICAL'],
  ['OBSERVATION', 'CLINICAL'],
  ['CLINICAL', 'CLINICAL'],
  ['NURSING', 'NURSING'],
  ['NURSE', 'NURSING'],
  ['TRANSPORT', 'TRANSPORT'],

  // 5. Facility functions. Last, because any of these words can appear inside
  //    the name of a client-facing service above.
  ['DOMESTIC', 'DOMESTIC'],
  ['CLEANING', 'DOMESTIC'],
  ['LAUNDRY', 'DOMESTIC'],
  ['HOUSEKEEP', 'DOMESTIC'],
  ['SHOPPING', 'DOMESTIC'],
  ['CATERING', 'CATERING'],
  ['COOK', 'CATERING'],
  ['CHEF', 'CATERING'],
  ['MEAL', 'CATERING'],
  ['KITCHEN', 'CATERING'],
  ['RECEPTION', 'ADMINISTRATION'],
  ['FRONT_DESK', 'ADMINISTRATION'],
  ['MAINTENANCE', 'MAINTENANCE'],
  ['GARDEN', 'MAINTENANCE'],
  ['CARE_ROUND', 'CARE_ROUND'],
];

/**
 * Place a raw service value into a display family.
 *
 * `serviceCode` is preferred when present because it is the more controlled of
 * the two columns; `serviceType` is the fallback. Returns `UNSET` only when both
 * are genuinely absent, and `UNKNOWN` when a value exists but is unrecognised.
 */
export function classifyRosterService(input: {
  readonly serviceCode?: string | null;
  readonly serviceType?: string | null;
}): RosterServiceFamily {
  const token =
    normaliseServiceToken(input.serviceCode) || normaliseServiceToken(input.serviceType);
  if (!token) return 'UNSET';

  const exact = EXACT_TOKENS[token];
  if (exact) return exact;

  for (const [needle, family] of SUBSTRING_RULES) {
    if (token.includes(needle)) return family;
  }
  return 'UNKNOWN';
}

/**
 * Stable index into a fallback palette for an unrecognised value.
 *
 * Same text always yields the same slot, so a service keeps its colour across
 * days and page loads. Callers pass their own palette length; this returns only
 * the index so the colour vocabulary stays with the presentation layer.
 */
export function unknownServicePaletteIndex(
  raw: string | null | undefined,
  paletteLength: number,
): number {
  if (paletteLength <= 0) return 0;
  const token = normaliseServiceToken(raw);
  let hash = 0;
  for (let i = 0; i < token.length; i += 1) {
    hash = (hash * 31 + token.charCodeAt(i)) >>> 0;
  }
  return hash % paletteLength;
}

/** Human-readable family name, for a legend or tooltip. */
export function rosterServiceFamilyLabel(family: RosterServiceFamily): string {
  switch (family) {
    case 'CLINICAL':
      return 'Clinical care';
    case 'NURSING':
      return 'Nursing';
    case 'PERSONAL_CARE':
      return 'Personal care';
    case 'DOMESTIC':
      return 'Domestic assistance';
    case 'SOCIAL_SUPPORT':
      return 'Social support';
    case 'ALLIED_HEALTH':
      return 'Allied health';
    case 'TRANSPORT':
      return 'Transport';
    case 'CARE_ROUND':
      return 'Care round';
    case 'CATERING':
      return 'Catering';
    case 'ADMINISTRATION':
      return 'Administration';
    case 'MAINTENANCE':
      return 'Maintenance';
    case 'INDEPENDENCE':
      return 'Independence';
    case 'EVERYDAY_LIVING':
      return 'Everyday living';
    case 'UNKNOWN':
      return 'Other service';
    case 'UNSET':
      return 'No service recorded';
  }
}
